import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import * as dashboardService from "../services/dashboard.services";
import { useTranslation } from "react-i18next";
import ReactApexChart from "react-apexcharts";
import dayjs from "dayjs";
import { ROLE } from "../../../utils/Constants";
import { getLocalKey } from "../../../utils/commonfunction";

const Dashboard = () => {
  const userData = getLocalKey("loggedInUser")
    ? JSON.parse(getLocalKey("loggedInUser"))
    : {};
  const chartRef = useRef(null);
  const { t } = useTranslation();
  const [filter, setFilter] = useState("last_month");
  const [showDashboardCount, setShowDashboardCount] = useState({});
  const [earningDateWise, setEarningDateWise] = useState([]);
  const [activeUsersDateWise, setActiveUsersDateWise] = useState([]);
  const isNotRunner = userData?.role !== ROLE.RUNNER;

  useEffect(() => {
    dashboardService
      .Counts()
      .then((response) => {
        setShowDashboardCount(response && response.data ? response.data : {});
      })
      .catch((error) => {
        console.log("error ====> ", error);
      });
  }, []);

  useEffect(() => {
    fetchCharts();
  }, [filter]);

  const fetchCharts = async () => {
    try {
      const [earningsRes, activeUsersRes] = await Promise.all([
        dashboardService.DateWiseEarnings(filter),
        dashboardService.DateWiseActiveUsers(filter),
      ]);

      setEarningDateWise(earningsRes?.data?.date_wise_earnings || []);
      setActiveUsersDateWise(
        activeUsersRes?.data?.date_wise_active_users || [],
      );
    } catch (err) {
      console.log("Chart error:", err);
    }
  };

  const earningformattedCategories = earningDateWise.map((item) =>
    filter === "last_6_months" ||
    filter === "last_year" ||
    filter === "current_year"
      ? dayjs(item.date).format("MMM YYYY")
      : dayjs(item.date).format("DD MMM"),
  );
  const earningSeries = [
    {
      name: "Earnings",
      data: earningDateWise.map((item) => Number(item.totalAmount || 0)),
    },
    {
      name: "Transactions",
      data: earningDateWise.map((item) => Number(item.totalTransactions || 0)),
    },
  ];
  const earningChartFormatOptions = {
    chart: {
      id: "earnings_chartId",
      height: 280,
      type: "area",
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: true,
        offsetX: 0,
        offsetY: 0,
        tools: {
          download: false,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
          customIcons: [],
        },
        autoSelected: "zoom",
      },
    },
    dataLabels: {
      enabled: false,
    },
    // colors: ["#7e8af2"],
    colors: ["#7e8af2", "#4CAF50"],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        gradientToColors: ["#FDD39E"],
        inverseColors: true,
        opacityFrom: 0.7,
        opacityTo: 0.3,
        stops: [0, 100, 90],
      },
    },
    xaxis: {
      categories: earningformattedCategories,
      labels: {
        rotate: -45,
      },
    },
    // yaxis: {
    //     labels: {
    //         formatter: (value) => `€ ${value.toFixed(2)}`,
    //     },
    // },
    yaxis: [
      {
        title: { text: "Earnings (€)" },
        labels: {
          formatter: (value) => `€ ${value.toFixed(2)}`,
        },
      },
      {
        opposite: true,
        title: { text: "Transactions" },
        labels: {
          formatter: (value) => Math.round(value),
        },
      },
    ],
    // tooltip: {
    //     y: {
    //         formatter: (value) => `(€ ${value.toFixed(2)})`,
    //     },
    //     followCursor: true,
    //     intersect: false,
    // },
    tooltip: {
      y: {
        formatter: (value, { seriesIndex }) => {
          // 0 = Earnings, 1 = Transactions
          if (seriesIndex === 0) {
            return `€ ${value.toFixed(2)}`;
          }
          return Math.round(value);
        },
      },
      followCursor: true,
      intersect: false,
    },

    legend: {
      position: "top",
    },
    grid: {
      show: true,
      borderColor: "#EEE",
      strokeDashArray: 2,
      position: "back",
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    markers: {
      size: 3,
      colors: ["#fff"],
      strokeColors: ["#5A045A", "#008000"],
      strokeWidth: 1,
      fillOpacity: 1,
      hover: {
        size: undefined,
        sizeOffset: 0,
      },
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
  };

  const activeUsersformattedCategories = activeUsersDateWise.map((item) =>
    filter === "last_6_months" ||
    filter === "last_year" ||
    filter === "current_year"
      ? dayjs(item.date).format("MMM YYYY")
      : dayjs(item.date).format("DD MMM"),
  );
  const activeUsersSeries = [
    {
      name: "Active Users",
      data: activeUsersDateWise.map((item) =>
        Number(item.active_users_count || 0),
      ),
    },
  ];
  const activeUsersChartFormatOptions = {
    chart: {
      id: "active_users_chartId",
      height: 280,
      type: "area",
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: true,
        offsetX: 0,
        offsetY: 0,
        tools: {
          download: false,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
          customIcons: [],
        },
        autoSelected: "zoom",
      },
    },
    dataLabels: {
      enabled: false,
    },
    colors: ["#7e8af2"],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        gradientToColors: ["#FDD39E"],
        inverseColors: true,
        opacityFrom: 0.7,
        opacityTo: 0.3,
        stops: [0, 100, 90],
      },
    },
    xaxis: {
      categories: activeUsersformattedCategories,
      labels: {
        rotate: -45,
      },
    },
    yaxis: [
      {
        title: { text: "Active Users" },
        labels: {
          formatter: (value) => Math.round(value),
        },
      },
    ],
    tooltip: {
      y: {
        formatter: (value) => Math.round(value),
      },
      followCursor: true,
      intersect: false,
    },
    legend: {
      position: "top",
    },
    grid: {
      show: true,
      borderColor: "#EEE",
      strokeDashArray: 2,
      position: "back",
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    markers: {
      size: 3,
      colors: ["#fff"],
      strokeColors: ["#5A045A", "#008000"],
      strokeWidth: 1,
      fillOpacity: 1,
      hover: {
        size: undefined,
        sizeOffset: 0,
      },
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
  };

  return (
    <>
      <div className="row row-sm animation_fade">
        <div className="col-sm-12 col-lg-12 col-xl-12">
          {isNotRunner && (
            <>
              <div className="row row-sm">
                <div className="col-sm-12 col-md-6 col-lg-6 col-xl-4">
                  <div className="card custom-card">
                    <div className="card-body cp">
                      <Link to={`/admin/user-management/user/list/1`}>
                        <div className="card-item">
                          <div className="card-item-icon card-icon cp ">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="24"
                              className="bi bi-people-fill"
                              viewBox="0 0 16 16"
                            >
                              <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7Zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-5.784 6A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216ZM4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                            </svg>
                          </div>

                          <div className="d-flex justify-content-between">
                            <div>
                              <div className="card-item-title mb-2">
                                <label className="main-content-label tx-13 font-weight-bold mb-1">
                                  Users
                                </label>
                              </div>
                              <div className="card-item-body">
                                <div className="card-item-stat">
                                  <h4 className="font-weight-bold">
                                    <h4 className="font-weight-bold">
                                      {showDashboardCount &&
                                      showDashboardCount?.users?.total_users
                                        ? showDashboardCount?.users?.total_users
                                        : "0"}
                                    </h4>
                                  </h4>
                                </div>
                              </div>
                            </div>
                            <div className="d-flex gap-3">
                              <div className="card-item-title mb-2">
                                <label className="main-content-label tx-13 font-weight-bold mb-1">
                                  Active:{" "}
                                  {showDashboardCount?.users?.active_users || 0}
                                </label>
                              </div>
                              <div className="card-item-title mb-2">
                                <label className="main-content-label tx-13 font-weight-bold mb-1">
                                  In-Active:{" "}
                                  {showDashboardCount?.users?.inactive_users ||
                                    0}
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="col-sm-12 col-md-6 col-lg-6 col-xl-4">
                  <div className="card custom-card">
                    <div className="card-body cp">
                      <Link to={`../subscription/transaction/list/1`}>
                        <div className="card-item">
                          <div className="card-item-icon card-icon cp">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              className="bi bi-bookmarks-fill"
                            >
                              <path d="M2 4a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4ZM4 6v12h16V6H4Zm9 3h4v2h-4V9Zm0 4h4v2h-4v-2Z" />
                            </svg>
                          </div>

                          <div className="card-item-title mb-2">
                            <label className="main-content-label tx-13 font-weight-bold mb-1">
                              Total Revenue
                            </label>
                          </div>
                          <div className="card-item-body">
                            <div className="card-item-stat">
                              <h4 className="font-weight-bold">
                                {showDashboardCount?.transactions
                                  ?.total_revenue || "0"}
                              </h4>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="col-sm-12 col-md-6 col-lg-6 col-xl-4">
                  <div className="card custom-card">
                    <div className="card-body cp">
                      <Link to={`../referrals/history/list/1`}>
                        <div className="card-item">
                          <div className="card-item-icon card-icon cp">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              className="bi bi-bookmarks-fill"
                            >
                              <path d="M2 4a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4ZM4 6v12h16V6H4Zm9 3h4v2h-4V9Zm0 4h4v2h-4v-2Z" />
                            </svg>
                          </div>

                          <div className="card-item-title mb-2">
                            <label className="main-content-label tx-13 font-weight-bold mb-1">
                              Successfull Referrals
                            </label>
                          </div>
                          <div className="card-item-body">
                            <div className="card-item-stat">
                              <h4 className="font-weight-bold">
                                {showDashboardCount?.referrals
                                  ?.successfull_referrals_count || "0"}
                              </h4>
                            </div>
                          </div>
                          {/* <div className="d-flex gap-3">
                                                        <div className="card-item-title mb-2">
                                                            <label className="main-content-label tx-13 font-weight-bold mb-1">Questions Earning: {showDashboardCount?.referrals?.total_questions_earning || 0}</label>
                                                        </div>
                                                        <div className="card-item-title mb-2">
                                                            <label className="main-content-label tx-13 font-weight-bold mb-1">Reports Earning: {showDashboardCount?.referrals?.total_reports_earning || 0}</label>
                                                        </div>
                                                    </div> */}
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="col-sm-12 col-md-6 col-lg-6 col-xl-4">
                  <div className="card custom-card">
                    <div className="card-body cp">
                      <Link to={``}>
                        <div className="card-item">
                          <div className="card-item-icon card-icon cp">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              className="bi bi-bookmarks-fill"
                            >
                              <path d="M2 4a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4ZM4 6v12h16V6H4Zm9 3h4v2h-4V9Zm0 4h4v2h-4v-2Z" />
                            </svg>
                          </div>

                          <div className="card-item-title mb-2">
                            <label className="main-content-label tx-13 font-weight-bold mb-1">
                              Online Users
                            </label>
                          </div>
                          <div className="card-item-body">
                            <div className="card-item-stat">
                              <h4 className="font-weight-bold">
                                {showDashboardCount?.active_users
                                  ?.current_online_users_count || "0"}
                              </h4>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>

                {/* <div className="col-sm-12 col-md-6 col-lg-6 col-xl-4">
                                    <div className="card custom-card">
                                        <div className="card-body cp">
                                            <Link to={``}>
                                                <div className="card-item">
                                                    <div className="card-item-icon card-icon cp">
                                                        <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" fill="currentColor" className="bi bi-bookmarks-fill">
                                                            <path d="M2 4a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4ZM4 6v12h16V6H4Zm9 3h4v2h-4V9Zm0 4h4v2h-4v-2Z" />
                                                        </svg>
                                                    </div>

                                                    <div className="d-flex justify-content-between">
                                                        <div>
                                                            <div className="card-item-title mb-2">
                                                                <label className="main-content-label tx-13 font-weight-bold mb-1">Complaints/Tickets</label>
                                                            </div>
                                                            <div className="card-item-body">
                                                                <div className="card-item-stat">
                                                                    <h4 className="font-weight-bold">
                                                                        {(showDashboardCount?.tickets?.open_tickets_count || 0) + (showDashboardCount?.tickets?.in_progress_tickets_count || 0)}
                                                                    </h4>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="d-flex gap-3">
                                                            <div className="card-item-title mb-2">
                                                                <label className="main-content-label tx-13 font-weight-bold mb-1">Open: {showDashboardCount?.tickets?.open_tickets_count || 0}</label>
                                                            </div>
                                                            <div className="card-item-title mb-2">
                                                                <label className="main-content-label tx-13 font-weight-bold mb-1">In-Progress: {showDashboardCount?.tickets?.in_progress_tickets_count || 0}</label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Link>
                                        </div>
                                    </div>
                                </div> */}

                <div col-12>
                  <div className="row" style={{ marginBottom: "10px" }}>
                    <div className="col-auto">
                      <select
                        className="form-select cp"
                        onChange={(e) => setFilter(e.target.value)}
                        value={filter}
                      >
                        <option value="current_week">Current Week</option>
                        <option value="last_week">Last Week</option>
                        <option value="current_month">Current Month</option>
                        <option value="last_month">Last Month</option>
                        <option value="current_year">Current Year</option>
                        <option value="last_year">Last Year</option>
                        <option value="last_6_months">Last 6 Months</option>
                      </select>
                    </div>
                  </div>

                  {/* Earning chart */}
                  <ReactApexChart
                    key={`earnings-${filter}`}
                    // ref={chartRef}
                    options={earningChartFormatOptions}
                    series={earningSeries}
                    type="area"
                    height={350}
                    className="dayReport"
                  />
                  <h4 className="text-center">Earnings</h4>
                  <hr className="my-2 py-4"></hr>

                  {/* Active users chart */}
                  <ReactApexChart
                    key={`active-users-${filter}`}
                    // ref={chartRef}
                    options={activeUsersChartFormatOptions}
                    series={activeUsersSeries}
                    type="area"
                    height={350}
                    className="dayReport"
                  />
                  <h4 className="text-center">Active Users</h4>
                </div>
              </div>
            </>
          )}

          {!isNotRunner && (
            <>
              <div className="row row-sm">
                <div className="col-sm-12 col-md-6 col-lg-6 col-xl-4">
                  <div className="card custom-card">
                    <div className="card-body cp">
                      <Link to={`/admin/user-management/user/list/1`}>
                        <div className="card-item">
                          <div className="card-item-icon card-icon cp ">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="24"
                              className="bi bi-people-fill"
                              viewBox="0 0 16 16"
                            >
                              <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7Zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-5.784 6A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216ZM4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                            </svg>
                          </div>
                          <div className="d-flex justify-content-between">
                            <div>
                              <div className="card-item-title mb-2">
                                <label className="main-content-label tx-13 font-weight-bold mb-1">
                                  Total Completed Booking
                                </label>
                              </div>
                              <div className="card-item-body">
                                <div className="card-item-stat">
                                  <h4 className="font-weight-bold">
                                    <h4 className="font-weight-bold">
                                      {showDashboardCount &&
                                      showDashboardCount?.runner_completed_orders?.completed_orders_count
                                        ? showDashboardCount?.runner_completed_orders?.completed_orders_count
                                        : "0"}
                                    </h4>
                                  </h4>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="col-sm-12 col-md-6 col-lg-6 col-xl-4">
                  <div className="card custom-card">
                    <div className="card-body cp">
                      <Link to={`/admin/user-management/user/list/1`}>
                        <div className="card-item">
                          <div className="card-item-icon card-icon cp ">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="24"
                              className="bi bi-people-fill"
                              viewBox="0 0 16 16"
                            >
                              <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7Zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-5.784 6A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216ZM4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                            </svg>
                          </div>
                          <div className="d-flex justify-content-between">
                            <div>
                              <div className="card-item-title mb-2">
                                <label className="main-content-label tx-13 font-weight-bold mb-1">
                                  Total Assigned Booking
                                </label>
                              </div>
                              <div className="card-item-body">
                                <div className="card-item-stat">
                                  <h4 className="font-weight-bold">
                                    <h4 className="font-weight-bold">
                                      {showDashboardCount &&
                                      showDashboardCount?.runner_assigned_orders?.assigned_orders_count
                                        ? showDashboardCount?.runner_assigned_orders?.assigned_orders_count
                                        : "0"}
                                    </h4>
                                  </h4>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Dashboard;
